import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { waApi, type WaChat } from '../../api/whatsapp'
import { organizationsApi } from '../../api/contacts'
import { LeadSearchInput } from '../mail/LeadSearchInput'
import { CreateLeadModal } from '../../components/CreateLeadModal'

interface Lead { id: string; firstName: string; lastName: string; email: string }

/**
 * Shown right after a manual-number chat is opened that isn't linked to a lead.
 * The user either links an existing lead or creates a new one (pre-filled with
 * the typed number). Either way the chat is linked, then the modal closes.
 */
export function LinkOrCreateLeadModal({ companyId, chat, typedPhone, onClose, initialCreate, defaultName }: {
  companyId: string
  chat: WaChat
  typedPhone: string
  onClose: () => void
  initialCreate?: boolean
  defaultName?: string
}) {
  const qc = useQueryClient()
  const [picked, setPicked] = useState<Lead | null>(null)
  const [showCreate, setShowCreate] = useState(!!initialCreate)

  const { data: orgs = [] } = useQuery({
    queryKey: ['organizations', companyId],
    queryFn: () => organizationsApi.list(companyId, { limit: 500 }).then(r => r.data.data),
    enabled: showCreate,
  })

  const done = () => {
    qc.invalidateQueries({ queryKey: ['wa-chat', companyId, chat.id] })
    qc.invalidateQueries({ queryKey: ['wa-chats', companyId] })
    onClose()
  }

  const link = useMutation({
    mutationFn: (leadId: string) => waApi.linkLead(companyId, chat.id, leadId),
    onSuccess: done,
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to link lead'),
  })

  // The create-lead form is rendered as a SIBLING (not nested) so clicks inside it
  // — e.g. opening the organization dropdown — don't bubble to this overlay's onClose.
  if (showCreate) {
    return (
      <CreateLeadModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        companyId={companyId}
        orgs={orgs}
        defaultPhone={typedPhone}
        defaultFirstName={defaultName}
        onCreated={(newLead) => {
          setShowCreate(false)
          link.mutate(newLead.id)
        }}
      />
    )
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="w-[26rem] rounded-lg bg-white p-5 shadow-xl" onClick={e => e.stopPropagation()}>
        <h3 className="mb-1 font-semibold">Link this chat to a lead</h3>
        <p className="mb-4 text-sm text-gray-500">
          {typedPhone} is on WhatsApp. Connect this conversation to a lead, or create a new one.
        </p>

        <label className="label">Existing lead</label>
        <LeadSearchInput companyId={companyId} value={picked} onChange={setPicked} placeholder="Search lead…" />
        <button
          className="btn btn-primary btn-sm mt-2 w-full"
          disabled={!picked || link.isPending}
          onClick={() => picked && link.mutate(picked.id)}
        >
          Link to selected lead
        </button>

        <div className="my-3 flex items-center gap-2 text-xs text-gray-400">
          <span className="h-px flex-1 bg-gray-200" /> or <span className="h-px flex-1 bg-gray-200" />
        </div>

        <button className="btn btn-secondary btn-sm w-full" onClick={() => setShowCreate(true)}>
          Create a new lead
        </button>

        <div className="mt-4 flex justify-end">
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Skip for now</button>
        </div>
      </div>
    </div>
  )
}
